import { useState, useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { patientAuthSliceActions } from "./PatientAuthSlice";
import { useNavigate } from "react-router-dom";
import { resetFhirAuthContext } from "./FhirAuthProvider";
import {
  trackCurrentPageDuration,
  trackEvent,
} from "../../app-insights/AppInsights";

const TOKEN_EXPIRY_BUFFER = 5 * 60 * 1000; // 5 minutes in milliseconds

export const useTokenExpiry = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const tokenResponses = useAppSelector((state) => state.patientAuth);
  const userName = useAppSelector(
    (state) => state.patientProfileDetails.patientProfileInfo.name
  );
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [remainingMinutes, setRemainingMinutes] = useState<number | null>(null);
  const hasAlertBeenShown = useRef(false); // Prevents repeated alert on route change

  useEffect(() => {
    if (
      !tokenResponses ||
      !tokenResponses.accessToken ||
      !tokenResponses.tokenExpiry
    ) {
      return;
    }

    const tokenExpiryTime = tokenResponses.tokenExpiry * 1000; // Convert UNIX timestamp to milliseconds
    const currentTime = Date.now();
    const timeLeft = tokenExpiryTime - currentTime;

    if (timeLeft <= 0) {
      // Token already expired
      dispatch(patientAuthSliceActions.clearUser());
      resetFhirAuthContext();
      navigate("/");
      return;
    }

    // Set timeout for auto logout at exact expiry time
    const logoutTimeout = setTimeout(() => {
      trackCurrentPageDuration(userName);
      trackEvent("SessionTimeout", { userName });
      dispatch(patientAuthSliceActions.clearUser());
      resetFhirAuthContext();
      navigate("/");
    }, timeLeft);

    // Set timeout for alert before token expiration
    let alertTimeout: NodeJS.Timeout | null = null;

    if (timeLeft <= TOKEN_EXPIRY_BUFFER && !hasAlertBeenShown.current) {
      const minutes = Math.ceil(timeLeft / 60000);
      if (minutes > 0) {
        setIsAlertOpen(true);
        setRemainingMinutes(minutes);
        hasAlertBeenShown.current = true;
      }
    } else if (!hasAlertBeenShown.current) {
      alertTimeout = setTimeout(() => {
        const updatedTimeLeft = tokenExpiryTime - Date.now();
        const updatedMinutes = Math.ceil(updatedTimeLeft / 60000);

        if (updatedMinutes > 0) {
          setIsAlertOpen(true);
          setRemainingMinutes(updatedMinutes);
          hasAlertBeenShown.current = true;
        }
      }, timeLeft - TOKEN_EXPIRY_BUFFER);
    }

    return () => {
      clearTimeout(logoutTimeout);
      if (alertTimeout) clearTimeout(alertTimeout);
    };
  }, [tokenResponses, dispatch, navigate]);

  return {
    isAlertOpen,
    closeAlert: () => setIsAlertOpen(false),
    remainingMinutes,
  };
};
